import $ from '@sparta.global.vendors/jquery';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaValidationFramework from '@sparta.global.utilities/sparta-validation-framework-utility/dist/es6.js';
import spartaGlobalParamsUtility from '@sparta.global.utilities/sparta-global-params-utility/dist/es6.js';

/* eslint-disable brace-style */
/* eslint-disable max-len */


/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        Sparta Input Error Utility
 * @file        sparta-input-error-utility.js
 * @description Provides standard error messaging for Sparta inputs
 * @classdesc   Provides standard error messaging for Sparta inputs
 * @global      Creates a singleton of the global spartaInputErrorUtility class
 * @author      Kerry Boyce
 * @copyright   © 2016 Bank of America Corporation. All rights reserved.
 *
 * The Sparta Input Error Utility is used to show and hide the standard Sparta inline error
 * messaging experience, as designed by the global UX Standards Team.  When included, it will
 * automatically integrate with the Sparta Validation Framework, be subscribing to its validation
 * events, but the "updateInlineError" method may also be called directly, if needed.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

// default settings for ui layer class
const classDefaults = {
  initNamespace: 'sparta-input-error-utility',
  globalReady: 'SPARTA_GLOBAL_READY.spartaBootstrapUtility'
};

// empty variable to store a reference to a current instance of the utility
let instance;

/**
 * Get the global strings requried for use by this component
 * @returns Object of global strings
 */
function getGlobalStrings() {
  // Get global strings
  // Note: We're needing multiple data sets from the global JS file, so get the larger glob first
  let globalStrings = spartaGlobalParamsUtility.getSpaParams({
    defaultObj: {
      validationSingleErrorTitle: "1 item needs your attention",
      validationErrorTitle: "[errorMessages.length] items need your attention",
      btnHideErrors: "Hide additional errors",
      btnShowErrors: "Show additional errors"
    }
  });

  // Reduce the spaParam strings to just what we're using
  if (Object.keys(globalStrings).length > 0) {
    globalStrings = {
      validationSingleErrorTitle: globalStrings.validationErrors.validationSingleErrorTitle,
      validationErrorTitle: globalStrings.validationErrors.validationErrorTitle,
      ...globalStrings.formErrorMessaging
    };
  }
  return globalStrings;
}

/**
 * @class spartaInputErrorUtility
 * @classdesc Class for global implementation of the standard Sparta 2.0 inline error messaging
 * experience
 */
class SpartaInputErrorUtility {
  /**
   * @method
   * @name constructor
   * @desc Initialize an instance of the inline error messaging utility class
   * @param {Object} options - object of various options to override the classDefaults above
   */
  constructor(options) {
    // extend class options based on defaults and provided constructor options
    if (options) {
      this.options = Object.assign({}, classDefaults, options);
    }

    // make sure we've got a singleton using our instance variable from above
    if (!instance) {
      const errorsObject = {};
      const errorPromises = [];
      instance = this;
      this.options.$document.one(`${this.options.globalReady}.${this.options.initNamespace}`,
      // eslint-disable-next-line no-underscore-dangle
      this._addListeners()).bind(this);
      Object.assign(instance, {
        errorsObject,
        errorPromises
      });
    }
    return instance;
  }

  /**
   * @method
   * @name _addListeners
   * @desc subscribes to the Validation Framework Sparta events for validation failures and
   * successes
   */
  _addListeners() {
    /* subscribe to the Sparta Validation Framework event for failed inline validations */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.VALIDATION_FAILURE_EVENT, data => {
      const {
        data: input
      } = data;
      const {
        rule
      } = input;
      const errorObject = {
        [rule]: {
          valid: false,
          input
        }
      };
      instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);
      instance.errorPromises.push(instance.updateInlineError(true, data.data));
      instance.triggerCoremetricsEvent(data.data.rule, data.data.message);
    });

    /* subscribe to the Sparta Validation Framework event for successful inline validations */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.VALIDATION_SUCCESS_EVENT, data => {
      const {
        data: input
      } = data;
      const {
        rule
      } = input;
      const errorObject = {
        [rule]: {
          valid: true
        }
      };
      instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);
      instance.errorPromises.push(instance.updateInlineError(false, data.data));
    });

    /* subscribe to the Sparta Validation Framework event for failed required field checks */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.REQUIRED_FIELD_FAILURE_EVENT, data => {
      const {
        data: input
      } = data;
      const {
        rule
      } = input;
      const errorObject = {
        [rule]: {
          valid: false,
          input
        }
      };
      instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);
      instance.errorPromises.push(instance.updateInlineError(true, data.data));
      instance.triggerCoremetricsEvent(data.data.rule, data.data.message);
      data.data.field.on('change.requiredRecheck', () => {
        data.data.field.off('change.requiredRecheck');

        // eslint-disable-next-line no-unused-vars
        setTimeout(() => {
          spartaValidationFramework.fn.core.validateRequiredFields(data.data.field);
        }, 50);
      });
    });

    /* subscribe to the Sparta Validation Framework event for successful required field checks */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.REQUIRED_FIELD_SUCCESS_EVENT, data => {
      const {
        data: input
      } = data;
      const {
        rule
      } = input;
      const errorObject = {
        [rule]: {
          valid: true
        }
      };
      instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);
      instance.errorPromises.push(instance.updateInlineError(false, data.data));
    });
  }

  /**
   * @method
   * @name updateInlineError
   * @desc Updates an input and its label to either show or hide input errors.
   * @param {Boolean} bShowError - whether the error should be shown ('false' will hide it)
   * @param {Object} oErrorData - Collection of information about the error that was triggered in
   *                              the format:
   *                                  {jQuery Collection} field - the input that was validated
   *                                  {String} rule - the validation rule that was run
   *                                  {String} message - the error message to be shown (blank on
   *                                    success)
   *                                  {jQuery Collection} label - the label of input that was
   *                                    validated
   * @example spartaInputErrorUtility.updateInlineError(true, oErrorData);
   */
  updateInlineError(bShowError, oErrorData) {
    return new Promise(resolve => {
      // Determine the target error message container for the field */
      const sErrorMessageContainderId = `${oErrorData.field.attr('id')}_errorMessage`;
      const $errorMessageContainer = $(`#${sErrorMessageContainderId}`);
      // update dom classes
      // eslint-disable-next-line max-len
      const sInputValidationErrorClass = spartaValidationFramework.data.globals.INPUT_ERROR_BASE_CLASS + spartaValidationFramework.data.globals.VALIDATION_ERROR_SUFFIX;
      // eslint-disable-next-line max-len  
      const sRequiredFieldErrorClass = spartaValidationFramework.data.globals.INPUT_ERROR_BASE_CLASS + spartaValidationFramework.data.globals.REQUIRED_ERROR_SUFFIX;
      const iUpdateDelayLength = instance.isNavigationElement() ? 200 : 0;
      const bIsRequiredFieldUpdate = oErrorData.rule === 'required-field';
      // eslint-disable-next-line no-unused-vars  
      setTimeout(() => {
        if (bShowError) {
          $errorMessageContainer.html(oErrorData.message);
          $errorMessageContainer.attr('aria-hidden', false);
          oErrorData.field.attr('aria-invalid', true);
          if (!bIsRequiredFieldUpdate) {
            $(`#${oErrorData.label.attr('for')}`).focus();
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!(oErrorData.field.hasClass(sInputValidationErrorClass) || oErrorData.field.hasClass(sRequiredFieldErrorClass))) {
            oErrorData.field.removeAttr('aria-invalid');
            $errorMessageContainer.attr('aria-hidden', true);
            $errorMessageContainer.empty();
          }
        }
        if (oErrorData.field.hasClass('gis-mask')) {
          const sErrorClass = bIsRequiredFieldUpdate ? sRequiredFieldErrorClass : sInputValidationErrorClass;
          const $maskedSiblingInput = oErrorData.field.siblings('.gis-mask-value');
          $maskedSiblingInput.toggleClass(sErrorClass, bShowError);
          if (bShowError) {
            $maskedSiblingInput.attr('aria-invalid', true);
          } else {
            $maskedSiblingInput.removeAttr('aria-invalid');
          }
        }
        return resolve(true);
      }, iUpdateDelayLength);
    });
  }

  /**
   * @method
   * @name isNavigationElement
   * @desc Determines if the currently active page element is a navigation-type element.
   * @return {Boolean} - indicating if the currently active page element is a link, buttton, or "button-type" input
   * @example let bIsNavigationElement = spartaInputErrorUtility.isNavigationElement();
   */
  isNavigationElement() {
    const nHoveredElements = document.querySelectorAll(':hover');
    if (nHoveredElements.length > 0) {
      const nCurrentElement = nHoveredElements[nHoveredElements.length - 1];
      const sCurrentElementType = nCurrentElement.nodeName.toLowerCase();
      return sCurrentElementType === 'a' || sCurrentElementType === 'button' || sCurrentElementType === 'input' && nCurrentElement.type === 'button' || $(nCurrentElement).closest('a, button, input[type="button"]').length > 0;
    }
    return false;
  }

  /**
   * @method
   * @name triggerCoremetricsEvent
   * @desc Determines if the currently active page element is a navigation-type element.
   * @param {string} sFailedRule - the failed validation rule name (must be uniaue, per input))
   * @param {string} sDisplayedMessage - the error message that was displayed to the user
   * @example spartaInputErrorUtility.triggerCoremetricsEvent("required", "This is a required
   * field.");
   */
  triggerCoremetricsEvent(sFailedRule, sDisplayedMessage) {
    // Trigger Coremetrics error event, if Coremetrics is avalilable on the page
    // eslint-disable-next-line camelcase
    if (typeof bactm_captureCustomError === 'function') {
      // eslint-disable-next-line no-undef
      bactm_captureCustomError(sFailedRule, sDisplayedMessage);
    }
  }

  /**
   * @method
   * @name collectAndDisplayErrrors
   * @desc Accepts a form ID and a Sparta Messaging Utility instance and displays
   * relevant form errors for the user
   * @param {String} formContainerID string representation of the ID attribute for the form's container. The form level error message will be prepended to this element.
   * @param {Object} messagingBox initialized instance of the spartaMessagingUtility
   * @param {Object} [options] should error message receive focus. If given an object options included to control how focus set. hideErrorList that hides the individual input error messages and lists only the number of errors in the form. errorMessageTitle String to edit the output title of message utility. includeHiddenInputs includes the hidden elements for validation.
   * @param {String} [errorMessageTitle = ''] *deprecated* String to edit the output title of message utility. If string includes [errorMessages.length] this gets replaced with number of errors
   * @memberof SpartaInputErrorUtility
   */
  collectAndDisplayErrrors(formContainerID, messagingBox, options = {
    focus: true,
    hideErrorList: false,
    errorMessageTitle: '',
    includeHiddenInputs: false
  }, errorMessageTitle = '') {
    // Get global strings for use in message writing
    const globalStrings = getGlobalStrings();

    // Deprecate the last supplied option, prefer to include it in the options object
    if (errorMessageTitle === '' && typeof options.errorMessageTitle === 'string') {
      errorMessageTitle = options.errorMessageTitle;
    }

    // Backwards compatibility for accepting a simple boolean to set focus on error message box
    if (typeof options === 'boolean' && options) {
      options = {
        focus: true
      };
    }

    // if we don't have a form and an error message instance, we cannot proceed
    if (typeof formContainerID === 'string' && formContainerID !== '' && messagingBox) {
      return new Promise(resolve => {
        // get form from page
        const form = document.getElementById(formContainerID);
        if (form instanceof Element) {
          // get promises created to update error messages from instance
          // make sure all error messages are done being updated
          Promise.all(instance.errorPromises).then(() => {
            // get object of errors from instance
            const {
              errorsObject
            } = instance;
            // find form inputs that are invalid and not hidden
            let targetEls;
            // If there are specific targets configured
            if (options.targetElements) {
              // check to see the value is a NodeList or Array
              if (options.targetElements.length) {
                targetEls = Array.from(options.targetElements);
              }
              // Check to see if the value is a single element
              else if (options.targetElements instanceof Element) {
                targetEls = [options.targetElements];
              }
              // Display a warning in the console, if the value is invalid
              else {
                targetEls = [];
                console.warn("**WARNING** in sparta-input-error-utility:collectAndDisplayErrrors:options.targetElements must be a NodeList, and array of Elements, or a single Element.");
              }
              targetEls = targetEls.filter(el => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement || el instanceof HTMLFieldSetElement);
            }
            // Default to all inputs in the form container, if no specific target elements are configured
            else {
              targetEls = Array.from(form.querySelectorAll('input, select, textarea, fieldset'));
            }
            targetEls = options.includeHiddenInputs ? targetEls : targetEls.filter(element => element['ariaHidden'] !== "true" && element.offsetParent !== null);
            const invalidInputs = targetEls.filter(element => element['ariaInvalid'] === "true");
            // get array of Error Object keys so we can iterate and get # of errors
            // ensure our array is building ONLY targeted inputs
            // prevents messages from building up over multiple forms on same page
            const errorKeysArray = [];
            invalidInputs.forEach(thisInput => {
              const inputID = thisInput.getAttribute('id');
              errorKeysArray.push(inputID);
            });
            // generate Array of error messages to send to Messaging Utility
            // so that it generates a <ul>
            const errorMessages = errorKeysArray
            // filter the non-masked inputs from errorKeysArray
            .filter(value => !value.includes('-masked'))
            // filter out valid fields
            .filter(item => !Object.keys(errorsObject[item]).every(validation => errorsObject[item][validation].valid))
            // sort fields based on DOM source order
            .sort((a, b) => {
              const indexA = invalidInputs.findIndex(item => item.id === a);
              const indexB = invalidInputs.findIndex(item => item.id === b);
              return indexA - indexB;
            })
            // map through our sorted Array and generate Array of error messages for display
            .map(item => {
              // find an error for this input by iterating through the available validation keys
              const validationErrorKey = Object.keys(errorsObject[item]).find(validation => {
                // if we have a validation that is not valid
                if (!errorsObject[item][validation].valid) {
                  // return this item
                  return true;
                }
              });
              // get the validation object for this input
              const validationError = errorsObject[item][validationErrorKey].input;
              // clone the label so we can get the text from it
              // we deeply clone because these label elements can have several children
              const label = validationError.label[0].cloneNode(true);
              // get the required indicator container element if this label has one
              const requiredIndicator = label.querySelector('.spa-input-label--required');
              // get the ADA text container element if this label has one
              const adaLabel = label.querySelector('.ada-hidden');
              // remove the required indicator element
              if (requiredIndicator) requiredIndicator.parentNode.removeChild(requiredIndicator);
              // remove the ADA text element
              if (adaLabel) adaLabel.parentNode.removeChild(adaLabel);
              // return formatted error string prepended with field label
              return `<strong>${label.innerText.replaceAll('*', '').trim()}</strong>: ${validationError.message.trim()}`;
            }); // END errorMessages setter
            // destroy the error message if it existed previously
            messagingBox.destroy();
            if (errorMessages.length) {
              // get message from JCR attached to form as data attribute
              if (!options.errorMessageTitle && form.getAttribute('data-validation-errors-title')) {
                options.errorMessageTitle = form.getAttribute('data-validation-errors-title');
              } else if (!options.errorMessageTitle) {
                // get validation errors object from global params based on page language
                if (errorMessages.length > 0 && errorMessages.length === 1) {
                  options.errorMessageTitle = globalStrings && globalStrings.validationSingleErrorTitle ? globalStrings.validationSingleErrorTitle : '';
                } else {
                  options.errorMessageTitle = globalStrings && globalStrings.validationErrorTitle ? globalStrings.validationErrorTitle : '';
                }
              }
              // if msg title contains error msg length, update it with JS value
              // if not set from JCR or global param than we're getting it from function call
              options.errorMessageTitle = options.errorMessageTitle.replaceAll('[errorMessages.length]', errorMessages.length);
              // Create a reuseable button generator
              let showAdditionalErrors = false;
              const toggleTextShowHide = () => {
                return `
                  <i class="spa-icon ${showAdditionalErrors ? 'icon-chevron-up--light-blue' : 'icon-caret-right--light-blue'}"></i>
                  ${showAdditionalErrors ? globalStrings.btnHideErrors : globalStrings.btnShowErrors}
                `.trim();
              };
              // generate new error message and prepend it inside the form
              messagingBox.type('error').title(options.errorMessageTitle).message(errorMessages).prepend(`#${formContainerID}`, {
                listType: 'ordered',
                size: 'small'
              });
              // check to see if the focus is an object with options
              if (typeof options === 'object' && options !== null) {
                setTimeout(() => {
                  // get default target
                  let target = document.getElementById(`sparta-message-${messagingBox._options.uuid}`);
                  const targetMsgList = target && target.querySelector('.spartaMessage-list') ? target.querySelector('.spartaMessage-list') : undefined;
                  // Adjust msg list
                  if (targetMsgList && errorMessages.length > 3) {
                    // Append button after content
                    targetMsgList.insertAdjacentHTML('afterend', `<button class="spa-input-error-utility-button-show-hide spa-btn spa-btn--link" type="button">${toggleTextShowHide()}</button>`);
                    // Hide each list item past 2 by default
                    [...targetMsgList.querySelectorAll('li')].forEach((item, index) => {
                      if (index > 2) {
                        item.classList.add('spa-hidden');
                      }
                    });
                  }
                  // if custom target, get that
                  if (typeof options.focusTarget === 'string' && options.focusTarget !== '') {
                    if (target instanceof Element) {
                      target = target.querySelector(options.focusTarget) || target;
                    }
                    options.focus = true;
                  }
                  if (target instanceof Element) {
                    // Ensure target has a tabindex so it's focusable by JavaScript
                    target.setAttribute('tabindex', '-1');
                    // Add aria label to target
                    if (typeof options.adaLabel === 'string' && options.adaLabel !== '') {
                      target.setAttribute('aria-label', options.adaLabel.replaceAll('[errorMessages.length]', errorMessages.length));
                    }
                  }
                  // check if error messages need to be hidden from view
                  if (options.hideErrorList) {
                    const errorList = target.querySelector('.spartaMessage-list');
                    if (errorList instanceof Element) {
                      errorList.style.display = 'none';
                    }
                  }
                  // set focus to target
                  if (target && options.focus) {
                    setTimeout(() => {
                      target.focus();
                    }, 100);
                  }
                }, 100);
              }
              // Toggle show/hide buttons and show/hide errors
              setTimeout(() => {
                // Define things here to cache results, should be better performance than to do it all repeatedly in the event listener
                const selectorButtons = `#sparta-message-${messagingBox._options.uuid} .spartaMessage-content button.spa-input-error-utility-button-show-hide`;
                const btn = form.querySelector(selectorButtons); // The button itself

                // List items from fourth down
                const listItems = form.querySelectorAll(`#sparta-message-${messagingBox._options.uuid} .spartaMessage-content .spartaMessage-list > li:nth-child(n + 4)`);
                if (btn && listItems.length > 0) {
                  btn.addEventListener('click', () => {
                    // Update button text
                    showAdditionalErrors = !showAdditionalErrors;
                    btn.innerHTML = toggleTextShowHide();
                    // Toggle list item visability
                    [...listItems].forEach(b => {
                      b.classList.toggle('spa-hidden');
                    });
                  });
                }
              }, 125);
              return resolve(false);
            } // END errorMessages.length
            return resolve(true);
          });
        }
      });
    }
    return false;
  }
}

// options specific to the call we'll make to the constructor below
const options = {
  $window: $(window),
  $document: $(document)
};
const spartaInputErrorUtility = new SpartaInputErrorUtility(options);

export { spartaInputErrorUtility as default };
